import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { MarketToolsBar } from "@/components/MarketToolsBar";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import {
  PALSINO_DAILY_PACK_CAP,
  PALSINO_DAILY_SPINS,
  PALSINO_GUEST_SPINS,
  PALSINO_JACKPOT_SYMBOL,
  PALSINO_MAX_WIN,
  palsinoPayoutSentence,
} from "@/lib/palsino-shared";


import { DAILY_FREE_PACKS } from "@/lib/game-constants";
import { breadcrumbSchema } from "@/lib/schema";
import { ShareButton } from "@/components/ShareButton";

const SlotMachine = lazy(() => import("@/components/SlotMachine").then((m) => ({ default: m.SlotMachine })));

const FAQ: FaqItem[] = [
  {
    q: "What is Pal Stars Palsino?",
    a: `Palsino is a free casino-style slot machine on Pal Stars that pays out Palworld booster packs. The reels use real Palworld OFFICIAL CARD GAME scans, and matching three of a kind wins up to ${PALSINO_MAX_WIN} free booster packs, credited to your account instantly.`,
  },
  {
    q: "How many Palsino spins do I get?",
    a: `Every free Pal Stars account gets ${PALSINO_DAILY_SPINS} Palsino spins per day, resetting at 00:00 UTC, on top of the ${DAILY_FREE_PACKS} free daily booster packs. Spins are rate-limited and capped server-side, so scripting the page cannot earn extra packs.`,
  },
  {
    q: "How do Palsino packs get added to my account?",
    a: "Automatically. Spins are rolled and paid out on the server, so any packs you win land in your bonus pack balance the moment the reels stop — open them straight away on the pack opening page.",
  },
  {
    q: "What is the maximum Palsino win?",
    a: `The jackpot line is three ${PALSINO_JACKPOT_SYMBOL.rarity} ${PALSINO_JACKPOT_SYMBOL.label} reels, which pays the maximum ${PALSINO_MAX_WIN} booster packs. ${palsinoPayoutSentence()} A pair of Rare or better pays 1 pack; pairs of Common or Uncommon pay nothing. Total Palsino winnings are capped at ${PALSINO_DAILY_PACK_CAP} packs per account per UTC day.`,
  },
  {
    q: "Can I choose which Palsino reels to play?",
    a: "Yes. Palsino has three reel sets you can switch between at any time: TCG Card Reels (real Palworld OFFICIAL CARD GAME scans), Pal Sphere Reels (Pal Sphere through Legendary Sphere) and Palworld Egg Reels (every egg family up to the Huge Dragon Egg). All three pay out in free booster packs, each has its own payout table shown under the machine, and your choice is remembered on your device.",
  },
  {
    q: "Does Palsino involve real money or gambling?",
    a: "No. Palsino costs nothing, has no purchases, no wagering and no cash prizes — the only currency is free virtual booster packs inside the Pal Stars card game.",
  },
  {
    q: "Can I turn the Palsino sound off?",
    a: "Yes. The slot machine has its own volume slider and mute button next to the reels, and the setting is remembered on your device. No audio is created until you press Spin, so nothing ever autoplays.",
  },
  {
    q: "Can I play Palsino without an account?",
    a: `Yes — every first-time visitor gets ${PALSINO_GUEST_SPINS} free trial spins with no account needed. Packs you win on trial spins are held in your browser, and creating a free Pal Stars account unlocks them so you can open the packs and file the cards in your 108-pocket binder.`,
  },
  {
    q: "Pal Stars Palsino free slot machine guide and FAQ?",
    a: "This page is the Pal Stars Palsino free slot machine guide and FAQ: it explains daily spins, guest spins, payouts, reel choices, pack caps and why the game is free entertainment rather than gambling.",
  },
  {
    q: "When do my Palsino spins reset?",
    a: `Daily spins reset at 00:00 UTC. Once you run out, the Palworld casino-style slot machine shows a live countdown to the next reset. Bonus spins from puzzles, events or staff grants never expire and stay available until you use them.`,
  },
];

/** House rules shown on the page and mirrored by the server-side spin logic. */
const RULES: string[] = [
  `${PALSINO_GUEST_SPINS} free trial spins with no account; a free account gets ${PALSINO_DAILY_SPINS} spins every day.`,
  `Claim your ${PALSINO_DAILY_SPINS} daily spins with the Claim button above the reels — one claim per account per UTC day, and your live spin balance is shown next to it.`,
  "Daily spins reset at 00:00 UTC; bonus spins from puzzles, events or staff grants never expire.",

  "Three reel sets to pick from — TCG cards, Pal Spheres or Palworld eggs — each with its own payout table and the same 30-pack jackpot.",
  "Spins are rolled on the server — the reels only animate the result, so nothing can be scripted or replayed.",
  `Three of a kind pays that card's pack value, up to the ${PALSINO_MAX_WIN}-pack jackpot (three ${PALSINO_JACKPOT_SYMBOL.rarity} ${PALSINO_JACKPOT_SYMBOL.label}).`,
  "A pair of Rare or better pays 1 pack. Pairs of Common or Uncommon pay nothing.",
  `Winnings are capped at ${PALSINO_DAILY_PACK_CAP} packs per account per UTC day, and spins are rate-limited.`,
  "Wins are credited automatically to your bonus pack balance — trial-spin wins unlock when you create an account.",
  "No money, no purchases, no wagering and no cash prizes. The only currency is free virtual booster packs.",
];


export const Route = createFileRoute("/palsino")({
  head: () => ({
    meta: [
      { title: "Palsino - The Palworld Casino | Free Spins" },
      { name: "description", content: `Play Palsino, the free Palworld casino-style slot machine on Pal Stars. Get ${PALSINO_DAILY_SPINS} daily spins, no money, and win booster packs.` },
      { name: "keywords", content: "Palsino, Palworld casino, Palworld slot machine, free Palworld casino, Palworld booster pack casino, Pal Stars Palsino, free spins Palworld" },
      { property: "og:title", content: "Palsino - The Palworld Casino" },
      { property: "og:description", content: `Free no-money Palworld casino-style reels with ${PALSINO_DAILY_SPINS} daily spins and booster pack prizes.` },
      { property: "og:url", content: "https://palstars.com/palsino" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/palsino" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palsino", "/palsino"]]),
faqSchema(FAQ, "https://palstars.com/palsino")],
  }),
  component: PalsinoPage,
});

function PalsinoPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Free spins · no money · booster pack prizes</p>
        <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="mt-2 text-3xl font-bold">Palsino - The Palworld Casino</h1>
          <ShareButton title={"Palsino - The Palworld Casino — Pal Stars"} text={"Palsino — the free Palworld slot machine on Pal Stars."} path="/palsino" />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Palsino is a free Palworld casino-style slot machine with no real-money gambling, no purchases and no cash
          prizes. Pick TCG card reels, Pal Sphere reels or Palworld egg reels, then match three symbols to win up to
          {" "}{PALSINO_MAX_WIN} booster packs. A free account gets {PALSINO_DAILY_SPINS} daily spins, and every winning
          spin credits packs automatically. Palsino the Palworld casino experience is a free Palworld casino and
          Palworld casino slot machine for collecting virtual booster packs only.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/guides/$slug" params={{ slug: "pal-stars-palsino" }} className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Palsino guide &amp; FAQ
          </Link>
          <Link to="/pack-opening" className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
            Open your packs →
          </Link>
          <Link to="/rewards" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            More free pack challenges
          </Link>
          <Link to="/daily-puzzle" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Win spins on the daily Pal Puzzle
          </Link>
          <Link to="/leaderboard" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Weekly leaderboard
          </Link>

        </div>
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Palsino reels</h2><p className="mt-2 text-sm text-muted-foreground">Loading slot machine…</p></section>}><SlotMachine /></Suspense>

      <section aria-labelledby="palsino-rules" className="glass rounded-2xl border p-5 sm:p-6">
        <h2 id="palsino-rules" className="text-xl font-bold">Palsino rules</h2>
        <p className="aeo-answer mt-2 text-sm text-muted-foreground">
          Palsino house rules: {PALSINO_GUEST_SPINS} free Palworld casino trial spins without an account,
          {" "}{PALSINO_DAILY_SPINS} daily Palworld slot machine spins with a free account, three of a kind pays up to
          {" "}{PALSINO_MAX_WIN} booster packs, and winnings are capped at {PALSINO_DAILY_PACK_CAP} packs per UTC day.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          {RULES.map((r) => (
            <li key={r} className="flex gap-2">
              <span aria-hidden="true" className="text-primary">•</span>
              <span className="text-muted-foreground">{r}</span>
            </li>
          ))}
        </ul>
      </section>

      <MarketToolsBar />

      <FaqSection items={FAQ} heading="Palsino FAQ" />

    </div>
  );
}
